import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import css from './SectionAwardsMaybe.module.css';

const FallbackAwardIcon = () => (
  <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M20 20c-4 3-6 7-6 12M44 20c4 3 6 7 6 12M14 28c-3 0-5 2-5 5s2 5 5 5M50 28c3 0 5 2 5 5s-2 5-5 5"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    <path
      d="M32 14c-7 0-12 5.4-12 12 0 4.4 2.4 8.2 6 10.4l6 3.6 6-3.6c3.6-2.2 6-6 6-10.4 0-6.6-5-12-12-12z"
      stroke="currentColor"
      strokeWidth="2.5"
      fill="currentColor"
      fillOpacity="0.12"
    />
    <path
      d="M29 26l2 2 4-4"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M32 42v8M26 50h12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

const StarFill = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
  </svg>
);

const AwardCard = ({ award, rating, reviewCount }) => {
  const { title, iconDataUrl } = award;
  const hasRating = typeof rating === 'number' && rating > 0;
  const hasReviews = typeof reviewCount === 'number' && reviewCount > 0;
  const showStats = hasRating || hasReviews;

  return (
    <div className={css.card}>
      <div className={css.cardMain}>
        <span className={css.iconWrapper}>
          {iconDataUrl ? (
            <img src={iconDataUrl} alt="" className={css.iconImage} />
          ) : (
            <FallbackAwardIcon />
          )}
        </span>
        <div className={css.cardContent}>
          <p className={css.cardTitle}>{title}</p>
        </div>
      </div>

      {showStats ? (
        <>
          <span className={css.divider} aria-hidden="true" />
          <div className={css.stats}>
            {hasRating ? <span className={css.statsRating}>{rating.toFixed(2)}</span> : null}
            {hasRating ? (
              <span className={css.statsStars} aria-hidden="true">
                {[1, 2, 3, 4, 5].map(n => (
                  <StarFill key={n} />
                ))}
              </span>
            ) : null}
            {hasReviews ? (
              <span className={css.statsReviews}>
                <FormattedMessage
                  id="SectionAwardsMaybe.reviewCount"
                  values={{ count: reviewCount }}
                />
              </span>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
};

/**
 * Airbnb-inspired award badge section for the listing detail page.
 * Reads custom awards from author.attributes.profile.publicData.awards:
 *   Array<{ id: string, title: string, iconDataUrl?: string }>
 *
 * @component
 * @param {Object} props
 * @param {Object} props.author - The ensured author user object
 * @param {number} [props.rating] - Average review rating
 * @param {number} [props.reviewCount] - Total number of reviews
 */
const SectionAwardsMaybe = ({ author, rating, reviewCount }) => {
  const awards = author?.attributes?.profile?.publicData?.awards;
  if (!Array.isArray(awards) || awards.length === 0) return null;

  const validAwards = awards.filter(a => a && typeof a.title === 'string' && a.title.trim());
  if (validAwards.length === 0) return null;

  return (
    <section className={css.root}>
      {validAwards.map(award => (
        <AwardCard
          key={award.id || award.title}
          award={award}
          rating={rating}
          reviewCount={reviewCount}
        />
      ))}
    </section>
  );
};

export default SectionAwardsMaybe;
